import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import LinkButton from '../LinkButton';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import config from '../../../../config/config';

interface TextAndMediaSection {
  title?: string;
  description?: string;
  content?: string;
  media?: {
    url?: string;
    formats?: {
      large?: { url?: string };
    };
  };
  buttonLink?: {
    link?: string;
    buttonLabel?: string;
  };
  imagePlacement?: string;
  backgroundImage?: {
    url?: string;
  };
}

interface TextAndMediaProps {
  section?: TextAndMediaSection;
}

const TextAndMedia = ({ section }: TextAndMediaProps) => {
  if (!section) return null;

  const {
    title,
    description,
    content,
    media,
    buttonLink,
    imagePlacement,
    backgroundImage,
  } = section;

  // Determine order based on imagePlacement
  const isImageLeft = imagePlacement === 'left';
  const contentOrder = isImageLeft ? 2 : 1;
  const mediaOrder = isImageLeft ? 1 : 2;
  const backgroundUrl = backgroundImage
    ? `${config.CMS_BASE_URL}${backgroundImage.url}`
    : '';

  return (
    <Box
      sx={{
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        minHeight: '400px',
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: { xs: 2, md: 4 },
        }}
      >
        <Grid container spacing={4} alignItems="flex-start">
          <Grid item xs={12} md={6} order={{ xs: 1, md: contentOrder }}>
            {title && (
              <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
                {title}
              </Typography>
            )}
            {(content || description) && (
              <Typography variant="body1" component="div">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {content || description}
                </ReactMarkdown>
              </Typography>
            )}
            {buttonLink && (
              <Box sx={{ mt: 3 }}>
                <LinkButton
                  href={buttonLink.link}
                  sx={{
                    backgroundColor: '#243B7F',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1e2f66',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {buttonLink.buttonLabel}
                </LinkButton>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6} order={{ xs: 2, md: mediaOrder }}>
            {media ? (
              <Box
                component="img"
                src={`${config.CMS_BASE_URL}${
                  media.formats?.large?.url || media.url
                }`}
                alt={title || 'media'}
                sx={{ width: '100%', borderRadius: 2, objectFit: 'cover' }}
              />
            ) : null}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TextAndMedia;
